import { inject } from "@angular/core";
import { FraudService } from "./api/services/fraud.services";
import { catchError } from "rxjs/operators";
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from "@angular/router";
import { VariablesService } from "./partials/services/variables.service";
import { tap } from "rxjs";

const handlerResolverError = (router: Router) => async (error: any) => {
    await router.navigateByUrl('/something-went-wrong');
    throw error;
};

export const getAccountByUuid: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getAccountByUuid(uuid).pipe(catchError(handlerResolverError(router)));
}

export const inputAccountEntityMerchant = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    const variableService = inject(VariablesService);
    return inject(FraudService).getAccountByUuid(uuid).pipe(
        tap((account) => {
            switch (variableService.role()) {
                case 'SALES': {
                    switch (account.role) {
                        case "COMPLIANCE":
                        case "POLICE": {
                            router.navigateByUrl('/access-denied');
                        }
                    }
                    break;
                }
            }
        }),
        catchError(handlerResolverError(router))
    );
}

export const getDashboards = () => {
    const router: Router = inject(Router);
    return inject(FraudService).getDashboards().pipe(catchError(handlerResolverError(router)));
}

export const getCompanyDashboards = () => {
    const router: Router = inject(Router);
    return inject(FraudService).getCompanyDashboards().pipe(catchError(handlerResolverError(router)));
}

export const getOperationsByUuid: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getOperations(uuid).pipe(catchError(handlerResolverError(router)));
}

export const getReportsStatsByUuid: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getReportsStats(uuid).pipe(catchError(handlerResolverError(router)));
}

export const getTransactionStatsByUuid: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getTransactionsStats(uuid).pipe(catchError(handlerResolverError(router)));
}

export const getReferralsStatsByUuid: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getReferralsStats(uuid).pipe(catchError(handlerResolverError(router)));
}

export const getSubscriptionStatsByUuid: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getSubscriptionStats(uuid).pipe(catchError(handlerResolverError(router)));
}

export const getDailyGraphDashboard = (from: string, to: string): ResolveFn<any> => () => {
    const router: Router = inject(Router);
    return inject(FraudService).getDailyGraphDashboard(from, to).pipe(catchError(handlerResolverError(router)));
}
export const getMonthDashboard = (from: string, to: string): ResolveFn<any> => () => {
    const router: Router = inject(Router);
    return inject(FraudService).getMonthDashboard(from, to).pipe(catchError(handlerResolverError(router)));
}

export const getStopWalletById: ResolveFn<any> = ({ params: { id } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getStopWalletById(id).pipe(catchError(handlerResolverError(router)));
}

export const getStopWalletTransactionById: ResolveFn<any> = ({ params: { id } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getStopWalletTransactionById(id).pipe(catchError(handlerResolverError(router)));
}

export const getCompanyById: ResolveFn<any> = ({ params: { uuid } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getCompanyById(uuid).pipe(catchError(handlerResolverError(router)));
}

export const getAccountsForUser: ResolveFn<any> = ({ params: { accountId } }: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const router: Router = inject(Router);
    return inject(FraudService).getAccountsForUserOfAccount(accountId).pipe(catchError(handlerResolverError(router)));
}

/**
 * Conditional resolver that executes only for specific roles
 * @param allowedRoles - array of roles for which the resolver should execute
 * @param resolver - resolver function to be called
 */
export const conditionalResolver = (
    allowedRoles: string[],
    resolver: ResolveFn<any>
): ResolveFn<any> => {
    return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const variablesService = inject(VariablesService);
        const currentRole = variablesService.role();
        
        // If current role is not in the allowed list, return null
        if (!allowedRoles.includes(currentRole)) {
            return null;
        }
        
        // Otherwise call the original resolver
        return resolver(route, state);
    };
}
